"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConferenceOverview() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: paragraphRef,
    isVisible: paragraphVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Cinzel:wght@400;600;700&family=Cormorant:ital,wght@1,300&display=swap');

        #conference-overview {
          position: relative;
          overflow: hidden;
        }

        /* Layered background: deep forest green with radial light blooms */
        #conference-overview::before {
          content: '';
          position: absolute;
          inset: 0;
          background:
            radial-gradient(ellipse 80% 60% at 20% 30%, hsla(158, 60%, 28%, 0.6) 0%, transparent 70%),
            radial-gradient(ellipse 60% 80% at 80% 70%, hsla(168, 50%, 22%, 0.5) 0%, transparent 65%),
            radial-gradient(ellipse 100% 100% at 50% 50%, hsl(158, 54%, 13%) 0%, hsl(158, 54%, 18%) 100%);
          pointer-events: none;
          z-index: 0;
        }

        /* Noise grain texture overlay */
        #conference-overview::after {
          content: '';
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          opacity: 0.18;
          pointer-events: none;
          z-index: 0;
        }

        .co-inner {
          position: relative;
          z-index: 1;
        }

        /* Decorative top rule */
        .co-top-rule {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 2rem;
        }
        .co-top-rule span {
          display: block;
          height: 1px;
          width: 80px;
          background: linear-gradient(to right, transparent, hsla(50, 80%, 70%, 0.7));
        }
        .co-top-rule span:last-child {
          transform: scaleX(-1);
        }
        .co-top-rule .diamond {
          width: 7px;
          height: 7px;
          background: hsl(50, 80%, 70%);
          transform: rotate(45deg);
          flex-shrink: 0;
        }

        /* EILH eyebrow label */
        .co-eyebrow {
          font-family: 'Cinzel', serif;
          font-size: 0.7rem;
          letter-spacing: 0.35em;
          text-transform: uppercase;
          color: hsl(50, 80%, 70%);
          margin-bottom: 1.25rem;
          opacity: 0;
          transform: translateY(14px);
          animation: fadeUp 0.7s ease forwards 0.1s;
        }

        /* Main heading */
        .co-heading {
          font-family: 'Cinzel', serif;
          font-size: clamp(2.2rem, 5.5vw, 7rem);
          font-weight: 700;
          line-height: 1.1;
          color: #fff;
          letter-spacing: 0.04em;
          margin: 0 0 0.25rem;
          opacity: 0;
          transform: translateY(20px);
          animation: fadeUp 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards 0.25s;
        }

        /* Italic sub-heading  */
        .co-subheading {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(2.1rem, 2.5vw, 1.5rem);
          font-weight: 300;
          color: hsla(0, 0%, 100%, 0.6);
          margin-bottom: 2rem;
          opacity: 0;
          transform: translateY(16px);
          animation: fadeUp 0.8s ease forwards 0.4s;
        }

        /* Ornamental divider */
        .co-divider {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
          margin: 1.75rem auto 2rem;
          opacity: 0;
          animation: fadeUp 0.6s ease forwards 0.55s;
        }
        .co-divider-line {
          height: 1px;
          width: 100px;
          background: linear-gradient(to right, transparent, hsl(50, 70%, 65%));
        }
        .co-divider-line:last-child { transform: scaleX(-1); }
        .co-divider-ornament {
          display: flex;
          gap: 5px;
          align-items: center;
        }
        .co-divider-ornament .dot {
          width: 4px; height: 4px;
          border-radius: 50%;
          background: hsl(50, 70%, 65%);
        }
        .co-divider-ornament .dot.large {
          width: 6px; height: 6px;
          border: 1px solid hsl(50, 70%, 65%);
          background: transparent;
        }

        /* Body paragraph */
        .co-body {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(1.35rem, 1.8vw, 1.5rem);
          font-weight: 400;
          line-height: 1.9;
          color: hsla(0, 0%, 100%, 0.78);
          max-width: 680px;
          margin: 0 auto;
          opacity: 0;
          transform: translateY(16px);
          animation: fadeUp 0.85s ease forwards 0.65s;
        }

        /* Date badge */
        .co-date-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-top: 2.5rem;
          padding: 0.55rem 1.4rem;
          border: 1px solid hsla(50, 70%, 65%, 0.45);
          border-radius: 2px;
          font-family: 'Cinzel', serif;
          font-size: 1.52rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: hsl(50, 80%, 70%);
          opacity: 0;
          animation: fadeUp 0.7s ease forwards 0.85s;
          position: relative;
          overflow: hidden;
        }
        .co-date-badge::before {
          content: '';
          position: absolute;
          inset: 0;
          background: hsla(50, 70%, 55%, 0.06);
        }
        .co-date-badge .sep {
          width: 1px;
          height: 14px;
          background: hsla(50, 70%, 65%, 0.4);
        }

        /* Corner decorations */
        .co-corner {
          position: absolute;
          width: 60px;
          height: 60px;
          pointer-events: none;
          z-index: 2;
          opacity: 0.35;
        }
        .co-corner--tl { top: 32px; left: 32px; border-top: 1px solid hsl(50,70%,65%); border-left: 1px solid hsl(50,70%,65%); }
        .co-corner--tr { top: 32px; right: 32px; border-top: 1px solid hsl(50,70%,65%); border-right: 1px solid hsl(50,70%,65%); }
        .co-corner--bl { bottom: 32px; left: 32px; border-bottom: 1px solid hsl(50,70%,65%); border-left: 1px solid hsl(50,70%,65%); }
        .co-corner--br { bottom: 32px; right: 32px; border-bottom: 1px solid hsl(50,70%,65%); border-right: 1px solid hsl(50,70%,65%); }

        /* Floating orb glows */
        .co-glow {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(70px);
          z-index: 0;
        }
        .co-glow--a {
          width: 340px; height: 340px;
          top: -60px; left: -80px;
          background: hsla(158, 60%, 35%, 0.35);
          animation: drift 10s ease-in-out infinite alternate;
        }
        .co-glow--b {
          width: 260px; height: 260px;
          bottom: -40px; right: -60px;
          background: hsla(170, 50%, 30%, 0.3);
          animation: drift 13s ease-in-out infinite alternate-reverse;
        }

        @keyframes drift {
          from { transform: translate(0, 0); }
          to   { transform: translate(30px, 20px); }
        }

        @keyframes fadeUp {
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <section id="conference-overview" className="w-full py-28 md:py-40">
        {/* Ambient glow orbs */}
        <div className="co-glow co-glow--a" aria-hidden="true" />
        <div className="co-glow co-glow--b" aria-hidden="true" />

        {/* Corner brackets */}
        <div className="co-corner co-corner--tl" aria-hidden="true" />
        <div className="co-corner co-corner--tr" aria-hidden="true" />
        <div className="co-corner co-corner--bl" aria-hidden="true" />
        <div className="co-corner co-corner--br" aria-hidden="true" />

        <div className="co-inner mx-auto max-w-4xl px-8 md:px-16 text-center">

          {/* Top decorative rule */}
          <div className="co-top-rule" aria-hidden="true">
            <span />
            <div className="diamond" />
            <span />
          </div>


          {/* Main heading */}
          <h1 ref={headingRef} className="co-heading">
            EILH Conference
          </h1>

          {/* Italic subtitle */}
          <p className="co-subheading">Overview</p>

          {/* Ornamental divider */}
          <div className="co-divider" aria-hidden="true">
            <div className="co-divider-line" />
            <div className="co-divider-ornament">
              <div className="dot" />
              <div className="dot large" />
              <div className="dot" />
            </div>
            <div className="co-divider-line" />
          </div>

          {/* Body copy */}
          <p ref={paragraphRef} className="co-body">
            EILH brings together innovators, researchers, and industry leaders for two days of
            cutting-edge discussions on technology, sustainability, and the future of innovation.
            Join us at Cal Poly, SLO for an unforgettable experience of learning, networking,
            and collaboration at the intersection of ideas that matter.
          </p>


        </div>
      </section>
    </>;
}